import type { CSSProperties, ReactNode } from 'react';
import { UMIcon } from '../../../urbanmap/_components';

/**
 * Contrôles du panneau Export mesurés sur tests.urbanmap.myengie.com (2026-10-15) :
 * `fluid-select`, `fluid-input` (recherche) et `fluid-tag--blue` (périmètre choisi).
 * Variantes locales : les UMSelect / UMInput / UMBadge partagés ne correspondent pas à ces mesures.
 */

const C = {
  grey0: 'var(--nj-core-color-grey-0, #ffffff)',
  grey200: 'var(--nj-core-color-grey-200, #f0f3f5)',
  grey300: 'var(--nj-core-color-grey-300, #e0e6eb)',
  grey400: 'var(--nj-core-color-grey-400, #9eadb8)',
  grey600: 'var(--nj-core-color-grey-600, #60798b)',
  blue200: 'var(--nj-core-color-blue-200, #e9f0fb)',
  blue300: 'var(--nj-core-color-blue-300, #c6dcf6)',
  blue400: 'var(--nj-core-color-blue-400, #7eaee9)',
  blue600: 'var(--nj-core-color-blue-600, #007bc5)',
  black: 'var(--nj-core-color-grey-1000, #000000)',
};

/** Les contrôles natifs n'héritent pas de la police Lato en prod : Arial 13.33px (défaut navigateur). */
const CONTROL: CSSProperties = {
  width: '100%',
  height: 32,
  boxSizing: 'border-box',
  border: `1px solid ${C.grey400}`,
  borderRadius: 0,
  fontFamily: 'Arial, sans-serif',
  fontSize: 13.3333,
  color: C.black,
  background: C.grey0,
  outline: 'none',
};

const CSS =
  `.prod-control:hover{background:${C.grey200}!important}` +
  `.prod-control:focus{border:2px solid ${C.blue600}!important;background:${C.grey0}!important}` +
  `.prod-control::placeholder{color:${C.grey600}}` +
  `.prod-tag-action:hover{background:${C.blue300}}.prod-tag-action:active{background:${C.blue400}}`;

export function ProdControlStyles() {
  return <style>{CSS}</style>;
}

export function ProdSelect({ value, options, onChange, ariaLabel }: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  ariaLabel?: string;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <select
        className="prod-control"
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...CONTROL, padding: '0 44px 0 16px', appearance: 'none', cursor: 'pointer' }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span style={{ position: 'absolute', right: 3, top: 5, width: 24, height: 24, pointerEvents: 'none' }}>
        <UMIcon name="expand_more" family="icons" size={24} color={C.black} />
      </span>
    </div>
  );
}

export function ProdSearchInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div style={{ position: 'relative' }}>
      <input
        className="prod-control"
        type="text"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...CONTROL, padding: '1px 16px' }}
      />
      <span style={{ position: 'absolute', right: 5, top: 'calc(50% - 10px)', width: 20, height: 20, pointerEvents: 'none' }}>
        <UMIcon name="search" family="icons" size={20} color={C.black} />
      </span>
    </div>
  );
}

/** `fluid-tag fluid-tag--blue` avec action "close". */
export function ProdTag({ children, onRemove, removeLabel }: { children: ReactNode; onRemove: () => void; removeLabel: string }) {
  return (
    <div
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: 4, lineHeight: '16px', fontSize: 14,
        background: C.blue200, border: `1px solid ${C.blue400}`, color: C.black,
      }}
    >
      <span>{children}</span>
      <button
        type="button"
        className="prod-tag-action"
        aria-label={removeLabel}
        title={removeLabel}
        onClick={onRemove}
        style={{ width: 16, height: 16, padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <UMIcon name="close" family="icons" size={16} color={C.black} />
      </button>
    </div>
  );
}
